import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';

import { CoinIcon } from '../components/CoinIcon.js';
import { Icon, type IconName } from '../components/Icon.js';
import { KbdKeys } from '../components/Kbd.js';
import { RichTextEditor, type RichTextEditorHandle } from '../components/RichTextEditor.js';
import { MessageBouncedError } from '../data/moderation.js';
import type { Attachment } from '../fixtures/domain.js';
import { EXPAND_COMPOSER, SEND_MESSAGE } from '../util/device.js';
import { ACCEPTED_EXTENSIONS, rejectAttachment } from './attachment-rules.js';
import { QuotedDraft, type QuotedPreview } from './MessageQuote.js';
import { useVoiceDraft, type VoiceContext } from './useVoiceDraft.js';
import { RecordingIndicator, VoicePlayer } from './VoicePlayer.js';
import { MAX_VOICE_NOTE_MS, MIC_PROBLEM_COPY, voiceFile, type VoiceClip } from './voiceNote.js';
import styles from './Composer.module.css';

export interface ComposerProps {
  /** Rejects if the message did not send, which is what puts the text back in
   *  the box. A caller that discards the rejection silently loses that. */
  onSend: (text: string, attachments?: Attachment[]) => Promise<string | undefined | void>;
  /** Upload a picked file and hand back the reference to attach. Omitted where
   *  attachments are not offered, which hides the File item entirely. */
  onUpload?: (file: File) => Promise<Attachment>;
  disabled?: boolean;
  placeholder?: string;
  rewardCoins?: number;
  onTemplates?: () => void;
  /** Opens the send-a-form dialog. Omitted where forms are not offered, which
   *  hides the Form item entirely. */
  onForm?: () => void;
  /** Opens the job-details picker; omitted hides the Job details item. */
  onPickJob?: () => void;
  /**
   * Withholds the expanded frame, leaving the one-line pill and nothing to press
   * for more room.
   *
   * Written as the exception because that is what it is: room to write is the
   * ordinary thing a composer should offer, and the widget is the surface where
   * it isn't. Half of a 400px panel is a reply floating over no conversation, and
   * a message long enough to need the room is what the full page is for — the
   * same reasoning as the rest of that panel's omissions. As an opt-out, a new
   * surface gets the frame by default and only a panel this small has to say no.
   */
  noExpand?: true;
  /** The message this draft answers or edits, staged by an action on a bubble. */
  quoted?: QuotedPreview;
  onCancelQuote?: () => void;
  /** Why the last send was refused, said instead of the generic failure. */
  notice?: string;
  /** Offers the mic (with `onUpload`) and labels its analytics. */
  voice?: VoiceContext;
}

export interface ComposerHandle {
  /** Drop text into the box, focused with the caret at the end — for a quick
   *  reply the employer should read and can edit before sending. */
  insert: (text: string) => void;
  /** Hand the caret over without touching what is already typed — for an offer
   *  to write your own, where clearing the draft would be the opposite of help. */
  focus: () => void;
  /** What is in the box right now, so an edit can hand it back afterwards. */
  value: () => string;
}

/**
 * Is this element one somebody is writing in? The document-level shortcut leaves
 * those to whoever owns them, unless the field is this composer's own.
 */
function isTyping(node: HTMLElement | null): boolean {
  if (!node) return false;
  return node.tagName === 'INPUT' || node.tagName === 'TEXTAREA' || node.isContentEditable;
}

/**
 * The canned openers on the + menu.
 *
 * Two questions an employer asks over and over, and the two that most often
 * stall a conversation while nobody types them out. They are inserted, never
 * sent: the draft lands in the box so it can be read, addressed to the
 * candidate by name, or cut down before it goes — same contract as a template
 * or a quick reply.
 *
 * A table because the menu is a list. A third question is a row here, not
 * another pair of buttons in the markup.
 */
const QUICK_MESSAGES: { label: string; icon: IconName; text: string }[] = [
  {
    label: 'Request Resume',
    icon: 'fileText',
    text: 'Hello! Your profile caught our attention. Could you please share an updated copy of your resume or CV?',
  },
  {
    label: 'Request Contact',
    icon: 'users',
    text: "Hello! We'd love to discuss this role with you further. Could you please share your phone number or preferred contact details?",
  },
];

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  {
    onSend,
    onUpload,
    disabled,
    placeholder,
    rewardCoins,
    onTemplates,
    onForm,
    onPickJob,
    noExpand,
    quoted,
    onCancelQuote,
    notice,
    voice,
  },
  ref,
) {
  const [text, setText] = useState('');
  const [failed, setFailed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [attachError, setAttachError] = useState('');
  /**
   * One line, or the whole space the thread was using.
   *
   * Two fields rather than one that grows: expanded, the draft is a
   * contenteditable with a formatting toolbar, and collapsed it is the plain
   * textarea it always was. What crosses between them is the markup string —
   * which is also what gets sent, so the handover cannot lose anything the
   * message could have carried.
   */
  const [expanded, setExpanded] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<RichTextEditorHandle>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const voiceUpload = useRef<{ clip: VoiceClip; uploaded: Attachment }>(null);
  const voiceDraft = useVoiceDraft(voice, async (clip) => {
    if (!onUpload) throw new Error('no upload');
    const uploaded =
      voiceUpload.current?.clip === clip
        ? voiceUpload.current.uploaded
        : await onUpload(voiceFile(clip));
    voiceUpload.current = { clip, uploaded };
    const voiceMeta = { durationS: clip.durationMs / 1000, waveform: clip.waveform };
    return (await onSend('', [{ ...uploaded, voice: voiceMeta }])) ?? undefined;
  });
  const showRewardHint = rewardCoins !== undefined && text === '';
  const label = placeholder ?? 'Write a message…';

  /** The draft as of this render, for the handover below to read on the way past. */
  const draft = useRef(text);
  draft.current = text;

  const focusInputEnd = () => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    // Next frame: the value React just set has not been committed yet, so
    // `el.value` is still the old one and the caret would land mid-text.
    requestAnimationFrame(() => el.setSelectionRange(el.value.length, el.value.length));
  };

  /**
   * Drop a whole message into the box, caret at the end and nothing sent.
   *
   * Both fields can be the one on screen, so both get written to. The + menu
   * rides in the expanded frame as well as the pill, and the handle below is
   * driven from the quick replies and the template panel — writing to only one
   * field would leave the draft sitting in whichever one is currently hidden.
   */
  const applyDraft = (value: string) => {
    setText(value);
    if (expanded) editorRef.current?.setMarkup(value);
    else focusInputEnd();
  };

  /**
   * Grow to fit, capped by the CSS max-height. Typing never needed this — a
   * quick reply, though, arrives whole, and routing it through the composer is
   * only useful if it can be read before it is sent, which a one-line window
   * doesn't allow.
   *
   * `expanded` is in the deps because collapsing mounts a brand new textarea:
   * the text didn't change, so without it a three-line draft would come back
   * into a one-line box and sit there scrolling until the next keystroke.
   */
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text, expanded]);

  /**
   * Hand the draft to whichever field has just taken over, caret at the end.
   *
   * Keyed on the expansion alone. While expanded the editor's own DOM *is* the
   * draft and `text` is only a read-out of it, so re-running this per keystroke
   * would rewrite the DOM under the caret — the exact bug `RichTextEditor`'s
   * doc comment is about. Hence `draft`, read through a ref: this needs the
   * current text without being subscribed to it.
   */
  const arrived = useRef(false);
  useLayoutEffect(() => {
    // The first pass is the composer arriving on screen, not the employer
    // choosing a field. Stealing focus there would pop the keyboard open on
    // every phone that so much as opened a chat.
    if (!arrived.current) {
      arrived.current = true;
      return;
    }
    if (expanded) editorRef.current?.setMarkup(draft.current);
    else focusInputEnd();
  }, [expanded]);

  useImperativeHandle(
    ref,
    () => ({
      insert: applyDraft,
      value: () => draft.current,
      focus: () => {
        // The expanded frame owns its own caret and has no handle for it, so
        // this focuses the plain box only. Nothing is cleared either way.
        if (!expanded) focusInputEnd();
      },
    }),
    [expanded],
  );

  /**
   * Change frame. The menu goes with it: one opened in the pill and left open
   * would re-appear anchored to the other frame's foot.
   */
  const setFrame = (next: boolean) => {
    setExpanded(next);
    setMenuOpen(false);
  };

  /** The box clears before the send resolves, so sending feels immediate.
   *  Upstream moderation makes a refusal a normal outcome, not only an outage. */
  const submit = () => {
    // An attachment on its own is a message. Requiring text would make the
    // picked file unsendable without inventing a caption for it.
    if (text.trim() === '' && !attachment) return;
    if (uploading) return;
    const pendingText = text;
    const pendingAttachment = attachment;
    setText('');
    setAttachment(null);
    setFailed(false);
    setAttachError('');
    onSend(pendingText, pendingAttachment ? [pendingAttachment] : undefined)
      .then(() => {
        // Back to one line once the message is gone for good. The expanded frame
        // is a means to writing one long message, and the thing worth looking at
        // afterwards is the thread it landed in — not a full-height empty editor
        // covering it. Collapsing also clears the editor, which unmounts with it.
        //
        // On the success path only, which is the whole reason this is not done
        // optimistically alongside the clear above: the frame belongs to the
        // draft, and a refused send means the draft is coming back. Collapsing
        // first would drop a ten-line message into a box that shows two of them,
        // and the employer would have to re-open the frame to carry on with a
        // message they never finished sending.
        setFrame(false);
      })
      .catch((error: unknown) => {
        // Both halves go back, or the employer loses the upload and has to find
        // the file again to retry what is usually a transient failure.
        setText(pendingText);
        setAttachment(pendingAttachment);
        // A bounce is explained by its card in the thread; the text is back to be rewritten.
        setFailed(!(error instanceof MessageBouncedError));
      });
  };

  /** Validate, upload, and stage. The input is reset either way so picking the
   *  same file twice still fires a change event. */
  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    // One `uploading` flag for one slot. Without this guard a second pick runs
    // concurrently, and whichever settles last wins — including after the
    // message was sent, which re-stages a file the employer already sent past.
    if (uploading) return;
    setAttachError('');
    if (!onUpload) {
      setAttachError('Attachments are not available here.');
      return;
    }
    const rejection = rejectAttachment(file);
    if (rejection) {
      setAttachError(rejection.message);
      return;
    }
    setUploading(true);
    try {
      setAttachment(await onUpload(file));
    } catch {
      setAttachError('Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  /**
   * The expansion shortcut, on the document rather than on the composer.
   *
   * It was scoped to the composer's own element first, on the principle that a
   * key which reshapes a panel from anywhere is a surprise. The principle holds
   * and the scope was still wrong: the whole point of the shortcut is to get room
   * *before* starting to write, and scoped that way it did nothing until the box
   * had already been clicked into — by which time reaching for the button next to
   * the caret would have been quicker. It read as a dead key.
   *
   * Guarded rather than global. A keystroke aimed at another field belongs to
   * that field — the inbox search is one ⌘E away from here — so anything typed
   * inside an input, a textarea or a contenteditable that is not this composer is
   * left alone. Everything else on the page (a click on the thread, a fresh
   * arrival with nothing focused) reaches it.
   */
  useEffect(() => {
    if (noExpand) return;

    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!EXPAND_COMPOSER.matches(event)) return;
      // Nothing to write means nothing to open. An open frame still has to
      // close, though, even on a chat that ended while it was open.
      if ((disabled || voiceDraft.busy) && !expanded) return;
      const target = event.target as HTMLElement | null;
      if (isTyping(target) && !rootRef.current?.contains(target)) return;
      event.preventDefault();
      setFrame(!expanded);
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded, disabled, noExpand, voiceDraft.busy]);

  /**
   * Escape, which stays on the composer's own element: a chat's dialogs and the
   * widget's panel both close on Escape from the document, and this one may only
   * answer for it while the reader is actually in here.
   *
   * Escape gives the frame back before it gives back any of the draft:
   * collapsing costs nothing, and dropping the quote costs something. So an
   * expanded composer collapses, and only a collapsed one lets Escape reach the
   * quote — the thing most recently attached to a draft that is otherwise all
   * typing.
   */
  const onFrameKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;

    if (expanded) {
      event.preventDefault();
      // The widget closes on a document-level Escape (see WidgetPanel's
      // `useDismiss`), which would take a half-written message with it.
      // Consuming the key here is what keeps the panel open, and it is why the
      // quote branch below stops it too.
      event.stopPropagation();
      setFrame(false);
      return;
    }
    if (quoted && onCancelQuote) {
      event.preventDefault();
      event.stopPropagation();
      onCancelQuote();
    }
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  /** The attachment control, identical in both frames. */
  const attach = (
    <div className={styles.plusWrap}>
      <button
        className={menuOpen ? styles.plusOpen : styles.plus}
        disabled={disabled || voiceDraft.busy}
        onClick={() => setMenuOpen((open) => !open)}
        title="Add attachment"
      >
        <Icon name="plus" size={22} />
      </button>
      {menuOpen && (
        <>
          <div className={styles.plusBackdrop} onClick={() => setMenuOpen(false)} />
          <div className={styles.plusMenu}>
            {onUpload && (
              <button
                className={styles.plusItem}
                disabled={uploading}
                onClick={() => {
                  setMenuOpen(false);
                  fileRef.current?.click();
                }}
              >
                <Icon name="file" size={16} />
                File
              </button>
            )}
            {onForm && (
              <button
                className={styles.plusItem}
                onClick={() => {
                  setMenuOpen(false);
                  onForm();
                }}
              >
                <Icon name="listChecks" size={16} />
                Form
              </button>
            )}
            <button
              className={styles.plusItem}
              onClick={() => {
                setMenuOpen(false);
                onTemplates?.();
              }}
            >
              <Icon name="template" size={16} />
              Template
            </button>
            {onPickJob && (
              <button
                className={styles.plusItem}
                onClick={() => {
                  setMenuOpen(false);
                  onPickJob();
                }}
              >
                <Icon name="briefcase" size={16} />
                Job details
              </button>
            )}
            <div className={styles.plusRule} />
            {QUICK_MESSAGES.map((quick) => (
              <button
                key={quick.label}
                className={styles.plusItem}
                onClick={() => {
                  setMenuOpen(false);
                  applyDraft(quick.text);
                }}
              >
                <Icon name={quick.icon} size={16} />
                {quick.label}
              </button>
            ))}
          </div>
        </>
      )}
      <input
        ref={fileRef}
        type="file"
        hidden
        accept={ACCEPTED_EXTENSIONS}
        onChange={(event) => {
          void pickFile(event.target.files?.[0]);
          // Reset so re-picking the same file still fires onChange.
          event.target.value = '';
        }}
      />
    </div>
  );

  const send = (className: string) => (
    <button
      className={className}
      onClick={voiceDraft.clip ? () => void voiceDraft.send() : submit}
      disabled={
        disabled ||
        (voiceDraft.clip ? voiceDraft.sending : uploading || (text.trim() === '' && !attachment))
      }
      title="Send"
      aria-label="Send"
      // Only true in the frame, where Enter is a line break and this is the
      // shortcut instead. In the pill, Enter itself sends, and Enter is not
      // something `aria-keyshortcuts` is for.
      aria-keyshortcuts={expanded ? SEND_MESSAGE.aria : undefined}
    >
      <Icon name="arrowUp" size={18} />
    </button>
  );

  /** What is going out alongside the words, in whichever frame is showing. */
  const attachRow = (attachment || uploading || attachError) && (
    <div className={styles.attachRow}>
      {uploading && <span className={styles.attachNote}>Uploading…</span>}
      {attachment && !uploading && (
        <span className={styles.attachChip}>
          <Icon name="paperclip" size={14} />
          <span className={styles.attachName}>{attachment.name}</span>
          <button
            className={styles.attachRemove}
            onClick={() => setAttachment(null)}
            title="Remove attachment"
            aria-label="Remove attachment"
          >
            <Icon name="close" size={12} />
          </button>
        </span>
      )}
      {attachError && <span className={styles.attachError}>{attachError}</span>}
    </div>
  );

  const micFace =
    voice !== undefined &&
    onUpload !== undefined &&
    voiceDraft.recorder.supported &&
    !quoted?.editing &&
    !voiceDraft.clip &&
    (voiceDraft.recording || (text.trim() === '' && !attachment && !uploading));
  const micLabel = voiceDraft.recording
    ? 'Stop recording'
    : voiceDraft.recorder.blocked
      ? 'Microphone blocked'
      : 'Record a voice note';
  const mic = (
    <button
      type="button"
      className={voiceDraft.recording ? styles.micRecording : styles.micPinned}
      style={
        voiceDraft.recording ? ({ '--cap': `${MAX_VOICE_NOTE_MS}ms` } as CSSProperties) : undefined
      }
      onClick={() => {
        if (voiceDraft.recording) return voiceDraft.recorder.stop();
        setMenuOpen(false);
        voiceDraft.start();
      }}
      disabled={(disabled && !voiceDraft.recording) || voiceDraft.recorder.phase === 'asking'}
      aria-label={micLabel}
      title={micLabel}
    >
      <Icon
        name={voiceDraft.recording ? 'stop' : voiceDraft.recorder.blocked ? 'micOff' : 'mic'}
        size={voiceDraft.recording ? 12 : 18}
      />
    </button>
  );
  const voiceRow = voiceDraft.recording ? (
    <div className={styles.voiceRowLive}>
      <RecordingIndicator elapsedMs={voiceDraft.recorder.elapsedMs} />
    </div>
  ) : (
    voiceDraft.clip && (
      <div className={styles.voiceRow}>
        <VoicePlayer
          src={voiceDraft.clip.url}
          durationS={voiceDraft.clip.durationMs / 1000}
          waveform={voiceDraft.clip.waveform}
        />
      </div>
    )
  );
  const voiceDiscardLabel = voiceDraft.recording ? 'Cancel recording' : 'Delete recording';
  const voiceDiscard = (
    <button
      type="button"
      className={voiceDraft.recording ? styles.grow : styles.voiceDelete}
      onClick={voiceDraft.discard}
      disabled={voiceDraft.sending}
      aria-label={voiceDiscardLabel}
      title={voiceDiscardLabel}
    >
      <Icon name={voiceDraft.recording ? 'close' : 'trash'} size={16} />
    </button>
  );

  const errorText =
    notice ??
    (voiceDraft.recorder.problem ? MIC_PROBLEM_COPY[voiceDraft.recorder.problem] : undefined) ??
    (failed || voiceDraft.failed ? 'Message not sent. Please try again.' : undefined);
  const errorBanner = errorText && <p className={styles.error}>{errorText}</p>;

  /**
   * The expanded frame: the same composer, unrolled.
   *
   * One box, and everything in it. The pill puts its controls inside its own
   * outline, so this does too — a send button under the box reads as the page's
   * button rather than this message's, which is exactly how it read when it was
   * out there.
   *
   * It takes a share of the pane rather than all of it (see `.expanded`). The
   * thread above stays on screen and stays pinned to its last message, so the
   * reply is still written in front of the thing it answers.
   */
  if (expanded) {
    return (
      <div ref={rootRef} className={styles.expanded} onKeyDown={onFrameKeyDown}>
        {errorBanner}
        <RichTextEditor
          ref={editorRef}
          onChange={(markup) => {
            setText(markup);
            setFailed(false);
          }}
          disabled={disabled}
          placeholder={label}
          onSubmit={submit}
          staged={
            (quoted && onCancelQuote) || attachRow ? (
              <div className={styles.stagedRow}>
                {quoted && onCancelQuote && (
                  <QuotedDraft quoted={quoted} onCancel={onCancelQuote} />
                )}
                {attachRow}
              </div>
            ) : undefined
          }
          foot={
            <div className={styles.frameFoot}>
              {attach}
              {/* Enter means something different in here, so the frame says so
                  rather than leaving it to be found out by a message that didn't
                  send. Hidden where there is no keyboard to press. */}
              <p className={styles.hint}>
                <KbdKeys keys={SEND_MESSAGE.keys} />
                <span>to send</span>
              </p>
              {send(styles.send!)}
            </div>
          }
          toolbarEnd={
            /* The far corner from Send, and that is the point: Send is the one
               button in here that cannot be taken back, and nothing you might
               mean to press instead should sit against it. Never disabled — the
               way out has to work even on a chat that has ended under you.

               Its hint slides out of it exactly as the expand button's does: the
               two are one toggle, and the way out should not be advertised more
               grudgingly than the way in was. No `title` here either. */
            <span className={styles.toggleWrap}>
              <span className={styles.toggleHint} aria-hidden="true">
                <KbdKeys keys={EXPAND_COMPOSER.keys} />
                <span>to collapse</span>
              </span>
              <button
                type="button"
                className={styles.frameToggle}
                onClick={() => setFrame(false)}
                aria-label="Collapse the composer"
                aria-keyshortcuts={`${EXPAND_COMPOSER.aria} Escape`}
              >
                <Icon name="collapse" size={15} />
              </button>
            </span>
          }
        />
      </div>
    );
  }

  return (
    // The plain variant is the one with a single button in the pill's corner; it
    // gives the input back the 32px the expand button would have stood in. See
    // `--send-clearance`.
    <div
      ref={rootRef}
      className={noExpand ? styles.composerPlain : styles.composer}
      onKeyDown={onFrameKeyDown}
    >
      {errorBanner}
      <div className={styles.inputRow}>
        {attach}
        <div className={rewardCoins !== undefined ? styles.pillReward : styles.pill}>
          {/* Inside the pill, above the caret: the quote is part of the message
              being written, not a banner about it. Sharing the input's own
              container is what says the two will be sent together. */}
          {quoted && onCancelQuote && <QuotedDraft quoted={quoted} onCancel={onCancelQuote} />}
          {attachRow}
          {/* The hint stands in for the placeholder, so it has to be anchored to
              the textarea rather than to the pill. Against the pill it was
              measured from the pill's top edge, which stopped being the top of
              the input the moment a quote could sit above it: the two rendered
              on top of each other. */}
          {voiceDraft.busy ? (
            voiceRow
          ) : (
            <div className={styles.inputArea}>
              {showRewardHint && (
                <span className={styles.rewardHint}>
                  Reply to get <CoinIcon size={15} /> {rewardCoins} credits
                </span>
              )}
              <textarea
                ref={inputRef}
                className={styles.input}
                rows={1}
                value={text}
                disabled={disabled}
                placeholder={rewardCoins !== undefined ? '' : label}
                onChange={(event) => {
                  setText(event.target.value);
                  setFailed(false);
                }}
                onKeyDown={onInputKeyDown}
              />
            </div>
          )}
          {!noExpand && !voiceDraft.busy && (
            <>
              {/* Left of Send, on Send's baseline. Ghost against Send's filled
                  disc: one of these two sends the message and the other only
                  changes the furniture, and that difference has to be readable
                  at a glance.

                  No `title`. The shortcut is the thing worth teaching here, and
                  it slides out of the button on approach instead of waiting a
                  second for the operating system to say it in a black box. The
                  keys are in `aria-keyshortcuts` for anyone not looking at the
                  screen, which is why the chips themselves are hidden. */}
              <button
                type="button"
                className={styles.grow}
                onClick={() => setFrame(true)}
                disabled={disabled}
                aria-label="Expand the composer"
                aria-keyshortcuts={EXPAND_COMPOSER.aria}
              >
                <Icon name="expand" size={15} />
              </button>
              {/* After the button in the DOM so plain CSS can react to hovering
                  it, and to its left on screen. */}
              <span className={styles.growHint} aria-hidden="true">
                <KbdKeys keys={EXPAND_COMPOSER.keys} />
                <span>to expand</span>
              </span>
            </>
          )}
          {voiceDraft.busy && voiceDiscard}
          {micFace ? mic : send(styles.sendPinned!)}
        </div>
      </div>
    </div>
  );
});
